import {
  R_ADMIN,
  R_EDITOR,
  R_OWNER,
  R_VIEWER,
} from '@components/Utilities/NamingConversions';
import { MISC_ROUTES } from '@components/Utilities/routePaths';
import { axiosTrackerConfigBuilder } from '..';

/**
 * Ensures the function only tries to track in production, and fails silently if an
 * error occurs. Used to run tracking methods as tracking is "best effort only".
 */
function safeExecuteProductionOnly(asyncFunction) {
  return async (...args) => {
    try {
      if (
        !window.location.host.includes('127.0.0.1') &&
        !window.location.host.includes('localhost')
      )
        return await asyncFunction(...args);
    } catch (error) {
      console.error('Tracking failed:', error);
    }
  };
}

class TrackerService {
  /**
   * @param {string} apiUrl - The API URL for tracking events (eg: https://analytics.arimadata.com).
   */
  constructor(apiUrl = MISC_ROUTES.ANALYTICS) {
    this.axiosInstance = axiosTrackerConfigBuilder(apiUrl);
    this.individualApiKey = process.env.REACT_APP_TRACKING_API_KEY_INDIVIDUAL;
    this.companyApiKey = process.env.REACT_APP_TRACKING_API_KEY_COMPANY;

    // Safe execution of the captureEvent, identifyUser, and setUserProperties methods.
    // Will execute in production only and fail silently if an error occurs.
    this.captureEvent = safeExecuteProductionOnly(
      this._captureEvent.bind(this)
    );
    this.identifyUser = safeExecuteProductionOnly(
      this._identifyUser.bind(this)
    );
    this.setUserProperties = safeExecuteProductionOnly(
      this._setUserProperties.bind(this)
    );
  }

  /**
   * Main capture event method used to capture an event "as an individual" and
   * once again "as a company" for individual level analytics and company wide
   * analytics.
   *
   * Use "this.captureEvent". eventName and userInfo are mandatory as we only
   * track registered users on the webapp.
   *
   * @param {string} eventName - The name of the event to track - eg; 'Login', 'Logout', 'Build MMM'
   * @param {Object} userInfo - The user info obj from getUserInfo()
   * @param {Object} extraProperties - Additional properties to overwrite default event/person properties.
   */
  async _captureEvent(eventName, userInfo, extraProperties = {}) {
    if (!eventName || !userInfo) {
      throw new Error('Event name and user info are required.');
    }

    // Track events for both the individual user and their company.
    const metadata = this._getUserMetadata();
    const timestamp = new Date().toISOString();

    const { email, company } = userInfo;
    const userProperties = {
      name: userInfo.name,
      role: (() => {
        switch (true) {
          case userInfo.roles.includes(R_ADMIN):
            return 'Super User';
          case userInfo.roles.includes(R_OWNER):
            return 'Owner';
          case userInfo.roles.includes(R_EDITOR):
            return 'Editor';
          case userInfo.roles.includes(R_VIEWER):
            return 'Viewer';
          default:
            return 'Custom';
        }
      })(),
      country_code: userInfo.settings?.referred_country_code,
    };
    const companyProperties = {
      expiration: userInfo.expiration,
      sub_crm: userInfo.sub_crm,
      sub_cross_md: userInfo.sub_cross_md,
      sub_loc_int: userInfo.sub_loc_int,
      sub_mmm: userInfo.sub_mmm,
      sub_persona: userInfo.sub_persona,
    };

    ////////////////////////////
    // Event for the INDIVIDUAL
    const individualEvent = {
      ////////////////////////////////
      // Section 1: Event Disposition
      event: eventName,
      distinct_id: email,
      timestamp,
      /////////////////////////////////////////////
      // Section 2: Define Properties
      properties: {
        // Event properties for the individual
        ...metadata,
        ...extraProperties,
        type: 'Individual', // Event type (same as person type) -- Cannot be overwritten
        // Person properties for the individual
        $set: {
          ...userProperties,
          company: company,
          active: true,
          ...(extraProperties.$set ?? {}),
        },
        $set_once: {
          user_email: email,
          ...(extraProperties.$set_once ?? {}),
          type: 'Individual', // Person type (same as event type) -- Cannot be overwritten
        },
      },
    };

    /////////////////////////
    // Event for the COMPANY
    const companyEvent = {
      ////////////////////////////////
      // Section 1: Event Disposition
      event: eventName,
      distinct_id: company,
      timestamp,
      /////////////////////////////////////////////
      // Section 2: Define Properties
      properties: {
        // Event properties for the company
        ...metadata,
        ...userProperties, // user that took the action
        user_email: email,
        ...extraProperties,
        type: 'Company', // Event type (same as person type) -- Cannot be overwritten
        // Person properties for the company
        $set: {
          active: true,
          ...companyProperties,
          ...(extraProperties.$set ?? {}),
        },
        $set_once: {
          company: company,
          ...(extraProperties.$set_once ?? {}),
          type: 'Company', // Person type (same as event type) -- Cannot be overwritten
        },
      },
    };

    // Individual event (event attached to an individual)
    const individualPayload = {
      api_key: this.individualApiKey,
      batch: [individualEvent],
      historical_migration: false,
    };

    // Company event (event attached to a company)
    const companyPayload = {
      api_key: this.companyApiKey,
      batch: [companyEvent],
      historical_migration: false,
    };

    try {
      await Promise.all([
        this.axiosInstance.post('/batch/', individualPayload),
        this.axiosInstance.post('/batch/', companyPayload),
      ]);
    } catch (error) {
      console.error('Capture event failed:', error);
    }
  }

  /**
   * Identifies a user and sets their properties. Used to move from anonymous to
   * logged-in state.
   *
   * Use "this.identifyUser", not "this._identifyUser".
   *
   * @param {string} distinctId - The user's unique identifier.
   * @param {Object} properties - The properties to set for the user.
   * @param {string} type - The type of user to identify (individual or company).
   */
  async _identifyUser(distinctId, properties, type = 'individual') {
    // set or update properties of a user.
    try {
      // Possibly need to change /capture/ endpoint to /i/v0/e/
      await this.axiosInstance.post('/capture/', {
        api_key:
          type === 'individual' ? this.individualApiKey : this.companyApiKey,
        event: '$identify',
        properties: {
          distinct_id: distinctId,
          ...properties,
        },
      });
    } catch (error) {
      console.error('Identify user failed:', error);
    }
  }

  /**
   * Sets or updates properties for a user. Allows you to send an update without
   * sending an event.
   *
   * Use "this.setUserProperties", not "this._setUserProperties".
   *
   * @param {string} distinctId - The user's unique identifier.
   * @param {Object} properties - The properties to set or update.
   * @param {string} type - The type of user to set properties for (individual or company).
   */
  async _setUserProperties(distinctId, properties, type = 'individual') {
    // It allows you to add or update properties for a user without sending an $identify event.
    try {
      // Possibly need to change /capture/ endpoint to /i/v0/e/
      await this.axiosInstance.post('/capture/', {
        api_key:
          type === 'individual' ? this.individualApiKey : this.companyApiKey,
        event: '$set',
        properties: {
          distinct_id: distinctId,
          ...properties,
        },
      });
    } catch (error) {
      console.error('Set user properties failed:', error);
    }
  }

  /**
   * Gets the current timestamp adjusted to the EST time zone.
   *
   * @returns {string} ISO timestamp in EST time zone.
   */
  getTimestamp() {
    const now = new Date();
    const estTime = new Date(
      // This will convert to string in EST timezone, so we need it back in date format.
      now.toLocaleString('en-US', { timeZone: 'America/Toronto' })
    );
    return estTime.toISOString();
  }

  ////////////////////
  // Helper Functions

  // Gets metadata about the user's environment for tracking purposes.
  _getUserMetadata() {
    return {
      $referrer: document.referrer || null,
      $user_agent: navigator.userAgent,
      $browser: this._getBrowserName(navigator.userAgent),
      $os: navigator.platform || null,
      $device_type: /Mobi|Android/i.test(navigator.userAgent)
        ? 'mobile'
        : 'desktop',
      $current_url: window.location.href,
      $current_url_host: window.location.host,
      $current_url_path: window.location.pathname,
      $screen_resolution: `${window.screen.width}x${window.screen.height}`,
      $viewport_dimensions: `${window.innerWidth}x${window.innerHeight}`,
      $color_depth: `${window.screen.colorDepth}-bit`,
      $timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
      $language: navigator.language || navigator.userLanguage,
    };
  }

  // Extracts the browser name from the user agent string.
  _getBrowserName(userAgent) {
    if (/firefox|fxios/i.test(userAgent)) return 'Firefox';
    if (/chrome|chromium|crios/i.test(userAgent)) return 'Chrome';
    if (/safari/i.test(userAgent)) return 'Safari';
    if (/edg/i.test(userAgent)) return 'Edge';
    if (/opera|opr/i.test(userAgent)) return 'Opera';
    return 'Unknown';
  }
}

export const trackerService = new TrackerService();
